import React from 'react';
import { Title1, Text, Button, Spinner, makeStyles, tokens } from '@fluentui/react-components';
import { useMsalAuth } from '../hooks/useMsalAuth';
import { createTmdsClient } from '../services/tmds';

const useStyles = makeStyles({
  root: {
    padding: tokens.spacingVerticalL,
    '@media (max-width: 768px)': { paddingTop: tokens.spacingVerticalS, paddingLeft: 0, paddingRight: 0 },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  actions: {
    marginTop: tokens.spacingVerticalM,
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  busyIndicator: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalSNudge,
  },
  section: {
    marginTop: tokens.spacingVerticalL,
  },
  codeBlock: {
    backgroundColor: tokens.colorNeutralBackground3,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    overflowX: 'auto',
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
  },
});

export default function Debug() {
  const { getToken } = useMsalAuth();
  const client = React.useMemo(() => createTmdsClient(getToken), [getToken]);

  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [token, setToken] = React.useState<string | null>(null);
  const [response, setResponse] = React.useState<unknown>(null);

  const fetchToken = async () => {
    setBusy(true);
    setError(null);
    try {
      const t = await getToken();
      setToken(t);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const testMyAccess = async () => {
    setBusy(true);
    setError(null);
    setResponse(null);
    try {
      const data = await client.getMyAccess<unknown>();
      setResponse(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const shortToken = token ? `${token.slice(0, 16)}… (${token.length} chars)` : 'No token';

  const styles = useStyles();

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Title1>Debug</Title1>
      </div>

      <Text>This page lets you quickly acquire a token and try a TMDS call.</Text>

      <div className={styles.actions}>
        <Button onClick={fetchToken} disabled={busy}>Get token</Button>
        <Button onClick={testMyAccess} disabled={busy}>Test /myaccess</Button>
        {busy && (
          <span className={styles.busyIndicator}>
            <Spinner size="small" /> <Text>Working…</Text>
          </span>
        )}
      </div>

      <div className={styles.section}>
        <Text weight="semibold">Token:</Text>
        <pre className={styles.codeBlock}>
          {shortToken}
        </pre>
      </div>

      <div className={styles.section}>
        <Text weight="semibold">Response:</Text>
        {error ? (
          <Text className={styles.errorText}>{error}</Text>
        ) : (
          <pre className={styles.codeBlock}>
            {response ? JSON.stringify(response, null, 2) : 'No response yet.'}
          </pre>
        )}
      </div>
    </div>
  );
}
